// EP-135 Tier 3 — Operator-side ballot record modal. Admin+ only.
// Writes a `cast_via: recorded` vote (§8b + D-6) — the customer-app
// `cast_via: self` path lives in a different controller.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { Button, Select } from '@/components/ui'
import { useCastRecordedVote } from '@/hooks/useGovernance'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { VoteChoice } from '@/types/governance'

export interface BallotFormModalProps {
  isOpen: boolean
  meetingId: number
  resolutionId: number
  attendees: Array<{ id: number; customer_name: string }>
  onClose: () => void
}

export function BallotFormModal({
  isOpen,
  meetingId,
  resolutionId,
  attendees,
  onClose,
}: BallotFormModalProps) {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')

  const [attendeeId, setAttendeeId] = useState<string>('')
  const [choice, setChoice] = useState<VoteChoice>('for')

  const cast = useCastRecordedVote(meetingId, resolutionId)

  const handleSave = async () => {
    const aid = Number(attendeeId)
    if (!aid || aid < 1) return
    try {
      await cast.mutateAsync({ attendee_id: aid, choice })
      setAttendeeId('')
      setChoice('for')
      onClose()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('votes.actions.record_vote')}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!attendeeId || Number(attendeeId) < 1}
            loading={cast.isPending}
            data-testid="btn-save-ballot"
          >
            {tc('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label={t('votes.actions.choose_attendee')}
          value={attendeeId}
          onChange={(e) => setAttendeeId(e.target.value)}
          options={[
            { value: '', label: t('votes.actions.choose_attendee') },
            ...attendees.map((a) => ({ value: String(a.id), label: a.customer_name })),
          ]}
          required
          data-testid="select-ballot-attendee"
        />
        <Select
          label={t('votes.labels.choice')}
          value={choice}
          onChange={(e) => setChoice(e.target.value as VoteChoice)}
          options={[
            { value: 'for', label: t('votes.choices.for') },
            { value: 'against', label: t('votes.choices.against') },
            { value: 'abstain', label: t('votes.choices.abstain') },
          ]}
          data-testid="select-ballot-choice"
        />
      </div>
    </Modal>
  )
}